/**
 * Selenological & Astrological Lunar Features Database
 * Curated with high-precision selenographic coordinates, geological eras,
 * mission history, and symbolic astrological archetypes.
 */

export const LUNAR_FEATURES = [
  // --- APOLLO CREWED LANDINGS ---
  {
    id: 'apollo-11',
    name: 'Apollo 11 — Tranquillity Base',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: 0.674,
    lon: 23.473,
    diameterKm: 0.5,
    depthKm: 0,
    geologicalEra: 'Late Imbrian (Mare Basalt)',
    date: 'July 20, 1969',
    astronauts: 'Neil Armstrong, Buzz Aldrin, Michael Collins (CMP in orbit)',
    description: 'The historic first human landing on another world. The Lunar Module "Eagle" touched down in the southern Mare Tranquillitatis.',
    scientificSignificance: 'Returned 21.55 kg of lunar samples, revealing high-titanium basalts dated at 3.6–3.8 Ga and establishing that the Moon experienced a primordial magma ocean.',
    astrologicalSignificance: 'Marked humanity\'s conscious psychological leap beyond Earth\'s gravitational cradle. In mundane astrology, associated with lunar awareness, visionary intuition, and collective soul memory.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Apollo 11 Tranquillity Base: Lunar Module Eagle descent stage and astronaut footpaths captured by LROC NAC.',
    keyLandmarks: ['Little West Crater', 'Passive Seismic Experiment (PSEP)', 'Laser Ranging Retroreflector (LRRR)', 'Eagle Descent Stage']
  },
  {
    id: 'apollo-12',
    name: 'Apollo 12 — Ocean of Storms',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: -3.012,
    lon: -23.422,
    diameterKm: 0.3,
    depthKm: 0,
    geologicalEra: 'Late Imbrian / Eratosthenian',
    date: 'November 19, 1969',
    astronauts: 'Pete Conrad, Alan Bean, Richard Gordon',
    description: 'Pinpoint precision landing within walking distance (180m) of the robotic Surveyor 3 lander, which had been exposed to lunar vacuum for 2.5 years.',
    scientificSignificance: 'Returned 34.3 kg of younger olivine-rich basalts (3.1–3.3 Ga) and retrieved pieces of Surveyor 3 to evaluate long-term space weathering on engineered materials.',
    astrologicalSignificance: 'Symbolizes precision, adaptability, and the reconnection with pioneering precursors in the oceanic depths of Oceanus Procellarum.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Oceanus Procellarum landing site near Surveyor 3.',
    keyLandmarks: ['Surveyor Crater', 'Head Crater', 'Bench Crater', 'Surveyor 3 Robot']
  },
  {
    id: 'apollo-14',
    name: 'Apollo 14 — Fra Mauro',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: -3.645,
    lon: -17.471,
    diameterKm: 0.4,
    depthKm: 0,
    geologicalEra: 'Early Imbrian (Ejecta Blanket)',
    date: 'February 5, 1971',
    astronauts: 'Alan Shepard, Edgar Mitchell, Stuart Roosa',
    description: 'Targeted the Fra Mauro ridge formation, the hummocky ejecta blanket excavated by the cataclysmic Mare Imbrium asteroid impact.',
    scientificSignificance: 'Gathered 42.3 kg of impact-melt breccias, dating the colossal Imbrium impact basin to ~3.85 billion years ago, a critical anchor for Solar System chronostratigraphy.',
    astrologicalSignificance: 'Alan Shepard hit two golf balls across the crater rim. Edgar Mitchell experienced a profound transcendent epiphanic oneness (the Overview Effect), founding noetic sciences.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Fra Mauro highlands and Cone Crater traverse.',
    keyLandmarks: ['Cone Crater (340m wide)', 'Fra Mauro Ridge', 'ALSEP Subsatellite Station']
  },
  {
    id: 'apollo-15',
    name: 'Apollo 15 — Hadley Rille & Apennines',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: 26.132,
    lon: 3.634,
    diameterKm: 1.2,
    depthKm: 0.3,
    geologicalEra: 'Imbrian / Pre-Nectarian Massif',
    date: 'July 30, 1971',
    astronauts: 'David Scott, James Irwin, Alfred Worden',
    description: 'The first J-series extended scientific mission, exploring the breathtaking foot of the 4,500m Montes Apenninus and the meandering canyon of Rima Hadley.',
    scientificSignificance: 'First deployment of the Lunar Roving Vehicle (LRV). Discovered the celebrated "Genesis Rock" (Sample 15415), a pristine 4.1-billion-year-old anorthosite fragment of the primordial lunar crust.',
    astrologicalSignificance: 'Confrontation with ancestral origins. The Genesis rock embodies the primal foundational archetype—the immortal plagioclase skeleton of the Moon.',
    reconImage: './assets/recon/hadley_rille.jpg',
    reconCaption: 'Apollo 15: Meandering sinuous canyon of Hadley Rille carved by ancient basaltic thermal erosion alongside Mount Hadley Delta.',
    keyLandmarks: ['Hadley Rille (canyon 1.5km wide, 300m deep)', 'Mount Hadley Delta (3,500m)', 'Silver Spur', 'St. George Crater']
  },
  {
    id: 'apollo-16',
    name: 'Apollo 16 — Descartes Highlands',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: -8.973,
    lon: 15.498,
    diameterKm: 0.8,
    depthKm: 0,
    geologicalEra: 'Nectarian (Cayley Formation)',
    date: 'April 21, 1972',
    astronauts: 'John Young, Charles Duke, Ken Mattingly',
    description: 'Exploration of the ancient lunar highlands, disproving pre-mission theories of explosive highland volcanism and revealing pervasive impact metamorphism.',
    scientificSignificance: 'Collected 95.7 kg of anorthosite breccias, establishing that the highlands were sculpted by repeated gargantuan impacts rather than rhyolitic volcanoes.',
    astrologicalSignificance: 'Represents the dispelling of illusions through empirical confrontation with stony truth; fortitude amidst highland starkness.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Descartes Highlands and North Ray Crater boulder fields.',
    keyLandmarks: ['House Rock (huge 12m boulder)', 'North Ray Crater', 'Stone Mountain']
  },
  {
    id: 'apollo-17',
    name: 'Apollo 17 — Taurus-Littrow Valley',
    type: 'apollo',
    category: 'Crewed Mission',
    lat: 20.191,
    lon: 30.772,
    diameterKm: 1.5,
    depthKm: 0.4,
    geologicalEra: 'Pre-Nectarian to Copernican',
    date: 'December 11, 1972',
    astronauts: 'Eugene Cernan, Harrison Schmitt (Professional Geologist), Ronald Evans',
    description: 'Final crewed lunar landing of the 20th century in a narrow valley flanked by the towering North and South Massifs.',
    scientificSignificance: 'Discovered famous "orange soil" at Shorty Crater—pyroclastic volcanic glass droplets erupted from fire fountains 3.64 Ga. Harrison Schmitt provided on-site expert field petrology.',
    astrologicalSignificance: 'The culmination of the classical Apollo epoch. Eugene Cernan left humanity\'s final footprints in the dust with a promise of peaceful return.',
    reconImage: './assets/recon/apollo17.jpg',
    reconCaption: 'Apollo 17: Scientist-Astronaut Harrison Schmitt examining a giant split boulder at the base of the North Massif in Taurus-Littrow.',
    keyLandmarks: ['Split Boulder (Station 6)', 'Shorty Crater (Orange Soil)', 'South Massif (2,300m)', 'Camelot Crater']
  },

  // --- HISTORIC ROBOTIC MISSIONS ---
  {
    id: 'luna-9',
    name: 'Luna 9 — First Soft Landing',
    type: 'robotic',
    category: 'Uncrewed Pioneer',
    lat: 7.08,
    lon: -64.37,
    diameterKm: 0.1,
    depthKm: 0,
    geologicalEra: 'Imbrian',
    date: 'February 3, 1966',
    astronauts: 'Robotic (Soviet OKB-1)',
    description: 'The first spacecraft to achieve a survivable soft landing on any extraterrestrial body and transmit circular panoramic television photos back to Earth.',
    scientificSignificance: 'Proved definitively that the lunar surface was firm and could support spacecraft weight, putting to rest the fear that landers would sink into deep dust.',
    astrologicalSignificance: 'Grounding the intangible dream into concrete tactile reality; the pioneer crossing the threshold of fear into physical certainty.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Oceanus Procellarum landing vicinity of the Luna 9 capsule.',
    keyLandmarks: ['Western Oceanus Procellarum plain', 'Planitia Descensus']
  },
  {
    id: 'change-4',
    name: 'Chang\'e 4 & Yutu-2 — Farside Pioneer',
    type: 'robotic',
    category: 'Farside Lander & Rover',
    lat: -45.457,
    lon: 177.589,
    diameterKm: 186,
    depthKm: 3.5,
    geologicalEra: 'Pre-Nectarian (South Pole-Aitken Basin)',
    date: 'January 3, 2019',
    astronauts: 'Robotic (CNSA)',
    description: 'First ever soft landing on the farside of the Moon, touching down inside Von Kármán crater within the vast South Pole-Aitken basin, communicating via Queqiao relay satellite.',
    scientificSignificance: 'Revealed low-frequency radio astronomy free from Earth electromagnetic interference and probed lunar mantle materials (pyroxene and olivine) excavated by ancient impacts.',
    astrologicalSignificance: 'Piercing the mystery of the Moon\'s hidden face—the unconscious, unreflected side that never directly beholds Earth. The revealer of deep subconscious shadows.',
    reconImage: './assets/recon/south_pole.jpg',
    reconCaption: 'Von Kármán crater inside the South Pole-Aitken basin on the Moon\'s farside.',
    keyLandmarks: ['Von Kármán Crater Floor', 'Yutu-2 Rover Tracks', 'Queqiao Halo Orbit Link']
  },
  {
    id: 'chandrayaan-3',
    name: 'Chandrayaan-3 — Shiv Shakti Point',
    type: 'robotic',
    category: 'Lunar South Pole Lander',
    lat: -69.373,
    lon: 32.319,
    diameterKm: 0.2,
    depthKm: 0,
    geologicalEra: 'Pre-Nectarian Highland',
    date: 'August 23, 2023',
    astronauts: 'Robotic (ISRO)',
    description: 'India\'s Vikram lander and Pragyan rover achieved the world\'s first successful soft landing in the lunar southern polar high latitudes.',
    scientificSignificance: 'Direct in-situ Laser-Induced Breakdown Spectroscopy (LIBS) confirmed the presence of sulfur, iron, titanium, and calcium in the polar regolith, and recorded thermal profiles across the topsoil.',
    astrologicalSignificance: 'Named Shiv Shakti, representing the primordial cosmic union of masculine consciousness (Shiva) and feminine generative power (Shakti). Resonates with renewal at the lunar frontier.',
    reconImage: './assets/recon/south_pole.jpg',
    reconCaption: 'High-latitude polar highlands surrounding Shiv Shakti Point.',
    keyLandmarks: ['Manzinus Crater Rim', 'Simpelius Crater Flank', 'Pragyan Rover Deployment Ramp']
  },

  // --- PROMINENT IMPACT CRATERS ---
  {
    id: 'crater-tycho',
    name: 'Tycho Crater',
    type: 'crater',
    category: 'Impact Crater',
    lat: -43.31,
    lon: -11.20,
    diameterKm: 86,
    depthKm: 4.8,
    geologicalEra: 'Copernican (~108 Million Years Ago)',
    description: 'The most prominent young impact crater on the Moon, renowned for its brilliant high-albedo ray system that stretches more than 1,500 km across the nearside.',
    scientificSignificance: 'Formed when an asteroid struck the southern highlands ~108 Ma (during Earth\'s Cretaceous dinosaur era). The central peak towers 2.4 km above the crater floor and exhibits a dramatic boulder field.',
    astrologicalSignificance: 'The dazzling radiant eye of the Moon. Astrologically represents sudden illumination, dramatic epiphanies, and the broadcast of psychic ripples across the emotional subconscious.',
    reconImage: './assets/recon/tycho.jpg',
    reconCaption: 'LROC NAC spectacular view of Tycho\'s dramatic 2-km central peak, showing shatter cones, impact-melt pools, and massive fractured boulders.',
    keyLandmarks: ['Central Peak Complex (2,400m tall)', 'Spectacular 1500km Ejecta Ray Web', 'Terraced Walls', 'Impact Melt Ponds']
  },
  {
    id: 'crater-copernicus',
    name: 'Copernicus Crater',
    type: 'crater',
    category: 'Impact Crater',
    lat: 9.62,
    lon: -20.08,
    diameterKm: 93,
    depthKm: 3.8,
    geologicalEra: 'Copernican (~800 Million Years Ago)',
    description: 'Called "The Monarch of the Moon" by lunar observers. A quintessential complex impact crater located in eastern Oceanus Procellarum with multiple tiered terraces and three central mountain peaks.',
    scientificSignificance: 'Defines the Copernican geological era. Its tiered collapse scarps preserve details of transient cavity collapse and rebound kinetics in hypervelocity impacts.',
    astrologicalSignificance: 'Named after Nicolaus Copernicus, who shifted humanity\'s worldview from geocentric to heliocentric. Symbolizes intellectual awakening and revolutionary clarity.',
    reconImage: './assets/recon/copernicus.jpg',
    reconCaption: 'Copernicus Crater: Majestic terraced walls, central uplift peaks, and vast hummocky ejecta blanket.',
    keyLandmarks: ['Triple Central Peaks (1,200m high)', 'Terraced Rim Scarps', 'Foshag Craterlet', 'Radial Ray Slumps']
  },
  {
    id: 'crater-aristarchus',
    name: 'Aristarchus Plateau & Crater',
    type: 'crater',
    category: 'Volcanic Plateau & Impact Crater',
    lat: 23.73,
    lon: -47.49,
    diameterKm: 40,
    depthKm: 3.7,
    geologicalEra: 'Copernican (~450 Million Years Ago)',
    description: 'The brightest prominent feature on the lunar nearside (albedo ~0.27, more than double the lunar average). Situated on an elevated volcanic plateau surrounded by Oceanus Procellarum.',
    scientificSignificance: 'Site of extensive pyroclastic glass deposits, deep sinuous rilles (Vallis Schröteri), and the most frequent historical reports of Transient Lunar Phenomena (TLP)—glows and radon gas outgassing.',
    astrologicalSignificance: 'The seat of mercurial luminescence and hidden alchemical fire. Connects with mental brilliance, visionary flashes, and the unveiling of hidden mysteries.',
    reconImage: './assets/recon/copernicus.jpg',
    reconCaption: 'Aristarchus crater with ultra-bright impact ejecta and the deep winding canyon of Vallis Schröteri.',
    keyLandmarks: ['Vallis Schröteri (Schröter\'s Valley)', 'Herodotus Crater', 'Aristarchus Central Peak', 'Pyroclastic Ash Plateau']
  },
  {
    id: 'crater-shackleton',
    name: 'Shackleton Crater — Lunar South Pole',
    type: 'crater',
    category: 'Polar Resource Zone',
    lat: -89.9,
    lon: 0.0,
    diameterKm: 21,
    depthKm: 4.2,
    geologicalEra: 'Late Imbrian',
    description: 'Perched precisely at the lunar South Pole. Because the Moon\'s rotational axis is tilted only 1.54°, the crater rim enjoys near-permanent sunlight, while the interior floor has been plunged in permanent darkness for billions of years.',
    scientificSignificance: 'The ultracold interior (-246°C / 27 K) acts as a cold trap preserving billions of tons of ancient water ice, volatile compounds, and organics. Primary target for NASA\'s Artemis III human landing.',
    astrologicalSignificance: 'The Axis Mundi of the Moon. A portal uniting eternal sunlight on the heights with infinite nocturnal silence in the depths. Symbol of timeless preservation and rebirth.',
    reconImage: './assets/recon/south_pole.jpg',
    reconCaption: 'Shackleton Crater at the Lunar South Pole: LOLA elevation map showing peaks of eternal light and permanently shadowed cryogenic depths.',
    keyLandmarks: ['Peaks of Eternal Light', 'Permanently Shadowed Regions (PSRs)', 'Water Ice Traps', 'Artemis Candidate Landing Zones']
  },
  {
    id: 'crater-tsiolkovsky',
    name: 'Tsiolkovsky Crater (Farside)',
    type: 'farside',
    category: 'Farside Landmark',
    lat: -20.4,
    lon: 129.1,
    diameterKm: 180,
    depthKm: 3.2,
    geologicalEra: 'Late Imbrian',
    description: 'The most spectacular crater on the Moon\'s farside. Features an intensely dark basaltic lava floor contrasting sharply against the brilliant white anorthositic highland walls, with a towering white central peak.',
    scientificSignificance: 'Named after astronautics pioneer Konstantin Tsiolkovsky. Proves that localized volcanism did erupt on the farside despite the 60km-thick crust that otherwise prevented vast maria from forming.',
    astrologicalSignificance: 'The hidden eye in the dark. Represents the soul\'s interior sanctuary, where hidden fire burns unseen by worldly observers.',
    reconImage: './assets/recon/tycho.jpg',
    reconCaption: 'Tsiolkovsky Crater: Unique dark mare flood surrounding an island of brilliant white anorthositic central peaks.',
    keyLandmarks: ['Dark Basaltic Floor (Mare Tsiolkovsky)', 'Prominent Anorthosite Central Peak', 'Waterman Crater', 'Farside Highland Rim']
  },

  // --- MAJOR MARIA (LUNAR SEAS) ---
  {
    id: 'mare-tranquillitatis',
    name: 'Mare Tranquillitatis (Sea of Tranquillity)',
    type: 'mare',
    category: 'Lunar Mare',
    lat: 8.5,
    lon: 31.4,
    diameterKm: 873,
    depthKm: 1.5,
    geologicalEra: 'Upper Imbrian (3.6–3.8 Ga)',
    description: 'An ancient basaltic plain distinguished by its deep, bluish-gray coloration due to high concentrations of ilmenite (iron-titanium oxide). Famous worldwide as the site of Apollo 11.',
    scientificSignificance: 'Its high-titanium basalts represent early-stage partial melting of the lunar mantle following the crystallization of the magma ocean.',
    astrologicalSignificance: 'The Sea of Tranquillity embodies deep emotional stillness, serenity, and the quiet reservoir where thoughts settle into crystalline understanding.',
    reconImage: './assets/recon/apollo11.jpg',
    reconCaption: 'Mare Tranquillitatis: Expansive basaltic plains and wrinkle ridges.',
    keyLandmarks: ['Tranquillity Base', 'Arago Wrinkle Ridge', 'Maskelyne Crater', 'Lamont Ring Structure']
  },
  {
    id: 'oceanus-procellarum',
    name: 'Oceanus Procellarum (Ocean of Storms)',
    type: 'mare',
    category: 'Vast Lunar Basin',
    lat: 18.4,
    lon: -57.4,
    diameterKm: 2568,
    depthKm: 2.0,
    geologicalEra: 'Procellarum KREEP Terrane (PKT)',
    description: 'The largest lunar mare, covering over 4 million square kilometers (more than 10% of the lunar surface). Dominates the western hemisphere of the nearside.',
    scientificSignificance: 'Home to the unique Procellarum KREEP Terrane—enriched in Potassium (K), Rare Earth Elements (REE), and Phosphorus (P), along with radioactive heat-producing Thorium and Uranium.',
    astrologicalSignificance: 'The wild, unbridled tempest of the subconscious. Represents oceanic emotion, tempestuous intuition, and the churning ocean of potentiality.',
    reconImage: './assets/recon/copernicus.jpg',
    reconCaption: 'Oceanus Procellarum: Endless volcanic mare floods dotted by Kepler and Marius Hills volcanic domes.',
    keyLandmarks: ['Marius Hills Volcanic Domes', 'Reiner Gamma Magnetic Swirl', 'Kepler Crater', 'Flamsteed Ring']
  },
  {
    id: 'mare-imbrium',
    name: 'Mare Imbrium (Sea of Showers)',
    type: 'mare',
    category: 'Impact Basin',
    lat: 32.8,
    lon: -15.6,
    diameterKm: 1145,
    depthKm: 3.5,
    geologicalEra: 'Early Imbrian (~3.85 Ga)',
    description: 'One of the largest impact basins in the Solar System, ringed by dramatic mountain chains: Montes Apenninus, Montes Caucasus, Montes Alpes, and Montes Jura.',
    scientificSignificance: 'Excavated by an asteroid ~250 km in diameter. The impact blast deposited ejecta across the entire nearside and triggered massive basaltic volcanic eruptions that lasted over 1.5 billion years.',
    astrologicalSignificance: 'The celestial forge. Archetype of profound transformation born through monumental disruption; the cauldron where raw experience crystallizes into enduring wisdom.',
    reconImage: './assets/recon/hadley_rille.jpg',
    reconCaption: 'Mare Imbrium basin bounded by the dramatic mountain scarp of Montes Apenninus.',
    keyLandmarks: ['Montes Apenninus (peaks to 5,000m)', 'Sinus Iridum (Bay of Rainbows)', 'Plato Crater', 'Archimedes Crater']
  },
  {
    id: 'mare-crisium',
    name: 'Mare Crisium (Sea of Crises)',
    type: 'mare',
    category: 'Circular Mascon Basin',
    lat: 17.0,
    lon: 59.1,
    diameterKm: 555,
    depthKm: 3.8,
    geologicalEra: 'Nectarian Basin, Imbrian Fill',
    description: 'An isolated circular mare located near the eastern lunar limb, easily spotted with the naked eye from Earth. Contains a strong positive gravitational anomaly (mascon).',
    scientificSignificance: 'Site of Soviet Luna 15, Luna 23, and Luna 24 missions. Luna 24 in 1976 successfully returned a 2-meter core sample demonstrating titanium-poor, water-bearing olivine basalts.',
    astrologicalSignificance: 'The threshold of crucible and discernment. Inspires resilience, navigating psychological crossroads, and transmuting acute crises into spiritual fortitude.',
    reconImage: './assets/recon/earthrise.jpg',
    reconCaption: 'Mare Crisium isolated circular basin on the eastern limb.',
    keyLandmarks: ['Promontorium Agarum', 'Yerkes Crater', 'Luna 24 Core Sample Site', 'Picard Crater']
  }
];

export const GEOLOGICAL_ERAS = [
  { name: 'Copernican', age: 'Present – 1.1 Ga', desc: 'Youngest impact craters with bright optical ray systems (e.g. Tycho, Copernicus). Minimal space weathering.' },
  { name: 'Eratosthenian', age: '1.1 – 3.2 Ga', desc: 'Impact craters whose rays have faded through solar wind micrometeorite bombardment. Late mare basalt flooding.' },
  { name: 'Upper Imbrian', age: '3.2 – 3.8 Ga', desc: 'Volcanic golden age: extensive effusive basalt eruptions filling impact basins to create the dark lunar maria.' },
  { name: 'Lower Imbrian', age: '3.8 – 3.85 Ga', desc: 'Formed by the colossal Mare Imbrium and Mare Orientale cataclysmic basin-forming impacts.' },
  { name: 'Nectarian', age: '3.85 – 3.92 Ga', desc: 'Late Heavy Bombardment epoch; excavation of the Nectaris, Crisium, and Serenitatis impact basins.' },
  { name: 'Pre-Nectarian', age: '3.92 – 4.51 Ga', desc: 'Primordial epoch of the Lunar Magma Ocean, crystallization of the anorthosite crust, and South Pole-Aitken basin impact.' }
];
